'use client';

import { useMemo } from 'react';

import { Spacer, SpacerSize, Title, TitleLevels, TitleMarkup } from '@bytel/trilogy-react';

import { ShortListContext } from '@app-types/eligibility';

import { LetterVariant } from '@components/letter-variant';

import { ERROR_CONTEXT, TECHNOLOGY } from '@constants/provider';

import { EligibilityResults, JourneyConfigType, isEligibleToFocusOffer } from '@eligibility/helpers';

interface EligibilityTitleProps {
    config: JourneyConfigType;
    shortList?: ShortListContext;
    results?: EligibilityResults;
}

export function EligibilityTitle({ config, shortList, results }: EligibilityTitleProps) {
    const title = useMemo(() => {
        if (results) {
            if (results.error === ERROR_CONTEXT.ERROR) {
                return config.wordings.mainTitle;
            }

            if (isEligibleToFocusOffer(shortList, results)) {
                return (
                    <>
                        Bonne n<LetterVariant letter='o' />
                        uvelle, vous pouvez profiter de la {shortList?.name}&nbsp;!
                    </>
                );
            } else {
                const techno = results.technology;

                if (Object.values(results.offers).length === 0) {
                    return <FibreUnavailable />;
                }

                switch (techno) {
                    case TECHNOLOGY.FTTH:
                        return (
                            <>
                                Bonne nouvelle, la fi
                                <LetterVariant letter='b' />
                                re est disponible&nbsp;!
                            </>
                        );
                    case TECHNOLOGY.BOX_4G:
                    case TECHNOLOGY.BOX_5G:
                    default:
                        return <FibreUnavailable />;
                }
            }
        } else if (shortList?.name) {
            return (
                <>
                    Vous avez ch
                    <LetterVariant letter='o' />
                    isi la {shortList.name}. <br />
                    On teste votre éligibilité&nbsp;?
                </>
            );
        } else {
            return config.wordings.mainTitle;
        }
    }, [results, shortList]);

    return (
        <>
            <Title
                markup={TitleMarkup.H1}
                level={TitleLevels.TWO}
                className='has-text-centered-tablet'
                data-cy='page-title'
            >
                {title}
            </Title>
            <Spacer size={SpacerSize.TWO} />
        </>
    );
}

const FibreUnavailable = () => {
    return (
        <>
            La fi
            <LetterVariant letter='b' />
            re n&apos;est pas encore disponible ici
        </>
    );
};
